<script lang="ts">
import { defineComponent } from "vue";
import type { PropType, ConferenceVotingRound, Locale } from "@/types";
import ContestSummaryView from "@/components/backend/ContestSummaryView.vue";

export interface ContestSelectionListContest {
  id: number;
  title: Record<string, string>;
  reference: string;
  label: string;
  description: Record<string, string>;
  options_count: number;
}

export default defineComponent({
  name: "ContestSelectionList",
  components: { ContestSummaryView },
  props: {
    scope: {
      type: String,
      required: true,
    },
    contests: {
      type: Array as PropType<ContestSelectionListContest[]>,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    enabledLocales: {
      type: Array as PropType<Locale[]>,
      required: true,
    },
    currentLocale: {
      type: String,
      required: true,
    },
    contestVotingRounds: {
      type: Array as PropType<ConferenceVotingRound[]>,
      required: true,
    },
  },
  data({ contestVotingRounds }) {
    return {
      wasValidated: false,
      selectedContestIds: contestVotingRounds.map((cvr: ConferenceVotingRound) => cvr.contest_id),
      errors: {
        contests: null,
      },
      contestFilter: "",
      debounceId: null,
      isHandRaise: false,
      elNodeObserver: null,
      el: null,
    }
  },
  watch: {
    isHandRaise: function(newState) {
      if (newState) {
        this.selectedContestIds = this.selectedContestIds.slice(0, 1);
      }
    }
  },
  computed: {
    contestFilterDebouncer: {
      get() {
        return this.contestFilter;
      },
      set(newValue: string) {
        this.debounce(() => this.contestFilter = newValue, 500)
      }
    },
    selectedContestIdsSet() {
      return new Set(this.selectedContestIds);
    },
    filteredContests() {
      if (this.contestFilter === "") return this.contests;

      const normalisedFilterValue = this.contestFilter.toLowerCase();
      return this.contests.filter((contest: ContestSelectionListContest) => {
        if (this.selectedContestIdsSet.has(contest.id)) return true;

        const searchedValue = contest.label.toLowerCase() + contest.title[this.displayLocale].toLowerCase();
        return searchedValue.includes(normalisedFilterValue);
      });
    },
    displayLocale() {
      return this.enabledLocales.includes(this.currentLocale) ? this.currentLocale : this.enabledLocales[0];
    },
    selectedQty() {
      return this.selectedContestIds.length;
    },
    deselectedContestVotingRounds() {
      return this.contestVotingRounds.filter((cvr: ConferenceVotingRound) =>
        !this.selectedContestIdsSet.has(cvr.contest_id));
    },
  },
  methods: {
    debounce(action: Function, delay: number = 1000) {
      if (this.debounceId) clearTimeout(this.debounceId);
      this.debounceId = setTimeout(action, delay);
    },
    errorMessagesFor(attribute: string) {
      return (this.errors[attribute] || []).map((e: string) => this.errorMessage(e)).join(" ");
    },
    errorMessage(error: string) {
      if (this.$te(`js.backend.voting_round_modal.errors.messages.${error}`))
        return this.$t(`js.backend.voting_round_modal.errors.messages.${error}`);
      else
        return error;
    },
    contestReference(contestId: number) {
      return this.contests.find((contest: ContestSelectionListContest) =>
        contest.id === contestId)?.reference;
    },
    contestPosition(contestId: number) {
      const index = this.selectedContestIds.indexOf(contestId);
      return index === -1 ? NaN : index + 1;
    },
    cvrId(contestId: number) {
      return this.contestVotingRounds.find((cvr: ConferenceVotingRound) =>
        cvr.contest_id === contestId)?.id
    },
    handleContest(contestRef: string) {
      const contestId = this.contests.find((contest: ContestSelectionListContest) =>
        contest.reference === contestRef).id
      if (this.selectedContestIds.includes(contestId)) {
        const index = this.selectedContestIds.indexOf(contestId);
        this.selectedContestIds.splice(index, 1);
      } else {
        this.selectedContestIds.push(contestId);
      }
    },
    handleContestPosition(contestRef: string, newPosition: number) {
      const contestId = this.contests.find((contest: ContestSelectionListContest) =>
        contest.reference === contestRef).id;
      const oldIndex = this.selectedContestIds.indexOf(contestId);
      const newIndex = Math.min(Math.max(1, newPosition), this.selectedQty) - 1;
      this.selectedContestIds.splice(oldIndex, 1);
      this.selectedContestIds.splice(newIndex, 0, contestId);
    },
    setError(attribute: string, type: string) {
      Object.assign(this.errors, {
        [attribute]: [...(this.errors[attribute] || []), type]
      });
    },
    selectAll() {
      this.selectedContestIds.push(
          ...this.contests
              .map((contest: ContestSelectionListContest) => contest.id)
              .filter((contestId: number) => !this.selectedContestIdsSet.has(contestId))
      );
    },
    deselectAll() {
      this.selectedContestIds = [];
    },
    contestPositions() {
      return Object.fromEntries(this.selectedContestIds.map((c: number, index: number) => [c, index + 1]));
    },
    toggleHandraise() {
      if (this.el.attributes["data-handraise"]) this.isHandRaise = true;
      else this.isHandRaise = false;
    },
  },
  mounted() {
    this.el = document.querySelector("[data-vue-component=contest-selection-list]");
    this.elNodeObserver = new MutationObserver(this.toggleHandraise);
    const config = { attributes: true };
    this.elNodeObserver.observe(this.el, config);
  },
  beforeUnmount() {
    this.elNodeObserver.disconnect();
  },
});
</script>

<template>
  <div class="mb-3">
    <label class="form-label">
      <span v-text="$t('js.backend.voting_round_modal.contests_title')" />
    </label>
    <div v-if="isHandRaise" class="alert alert-warning">
      <i class="fas fa-exclamation-triangle me-1"></i>
      It is only possible to select one contest per slide when the "Hand Raise" mode is enabled.
    </div>
    <div class="input-group mb-3 w-50">
      <span class="input-group-text"><i class="fas fa-search"></i></span>
      <input class="form-control" type="search" v-model="contestFilterDebouncer" @keydown.enter.prevent>
    </div>
    <div class="row">
      <div class="col-6 hstack">
        <p>{{ $t("js.backend.voting_round_modal.available_contests") }}</p>
        <button type="button" class="btn btn-light btn-sm ms-auto border-secondary mb-3" @click.prevent="selectAll"
                v-text="$t('js.backend.voting_round_modal.select_all_btn')" :disabled="selectedQty === contests.length || isHandRaise || locked" />
      </div>
      <div class="col-6 hstack">
        <p>{{ $t("js.backend.voting_round_modal.selected_contests") }}</p>
        <button type="button" class="btn btn-light btn-sm  ms-auto border-secondary mb-3" @click.prevent="deselectAll"
                v-text="$t('js.backend.voting_round_modal.deselect_all_btn')" :disabled="selectedQty === 0 || locked" />
      </div>
    </div>

    <input type="hidden" :name="`${scope}[contest_ids][]`" value="">
    <template v-for="contestId in selectedContestIds">
      <input type="hidden" :name="`${scope}[contest_ids][]`" :value="contestId">
    </template>

    <TransitionGroup name="selection-list" tag="div" class="contest-selection-list" :class="{ 'is-invalid': errors.contests }">
      <ContestSummaryView
          v-for="contest in filteredContests"
          :contest="contest"
          :currentLocale="displayLocale"
          :editMode="locked"
          :isSelected="selectedContestIdsSet.has(contest.id)"
          :key="contest.id"
          :position="contestPosition(contest.id)"
          :selectedQty="selectedQty"
          :disabled="isHandRaise && selectedQty === 1"
          :locked="locked"
          :isHandRaise="isHandRaise"
          @handleContest="handleContest"
          @handleContestPosition="handleContestPosition"
      />
    </TransitionGroup>
    <div class="invalid-feedback" v-if="errors.contests" v-text="errorMessagesFor('contests')" />
  </div>
</template>

<style lang="css" scoped>
.selection-list-move, /* apply transition to moving elements */
.selection-list-enter-active,
.selection-list-leave-active {
  transition: all 0.5s ease;
}

.selection-list-move {
}

.selection-list-enter-from,
.selection-list-leave-to {
  transform: translateX(100%);
  opacity: 0.0;
}

.selection-list-leave-active {
  position: absolute;
  width: calc(50% - 1.75rem);
  height: 4rem;
}

.contest-selection-list {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.25rem;
  grid-auto-flow: column;
  grid-auto-rows: 4rem;
  overflow-x: clip;
  overflow-y: auto;
  height: 30rem;
}
</style>
